import { useState } from "react";
import { X, Check, CheckCircle2, Save, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * ChecklistRunModal — the runtime view: shows the run's items, lets the
 * captain tick each one off (with an optional note), and finalizes.
 * Ticks are persisted per-item via PATCH so a partial state is never lost
 * (e.g., if the captain closes the modal halfway through winterizing).
 */
export default function ChecklistRunModal({ run, onClose, onChange }) {
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [expandedItem, setExpandedItem] = useState(null);
    const [noteDrafts, setNoteDrafts] = useState({});

    const isComplete = run.status === "complete";
    const done = run.ticks.filter((t) => t.done).length;
    const total = run.ticks.length;
    const pct = total ? Math.round((done * 100) / total) : 0;

    const tick = async (itemId, done, note) => {
        setSaving(true);
        setErr("");
        try {
            const { data } = await api.patch(`/checklist-runs/${run.id}/tick/${itemId}`, {
                done,
                note: note || null,
            });
            onChange(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e));
        } finally {
            setSaving(false);
        }
    };

    const finalize = async () => {
        setSaving(true);
        setErr("");
        try {
            const { data } = await api.post(`/checklist-runs/${run.id}/complete`);
            onChange(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="checklist-run-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: "fixed", inset: 0, zIndex: 60,
                background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 16, overflowY: "auto",
            }}
        >
            <div
                className="bm-card"
                style={{
                    width: "100%", maxWidth: 620, maxHeight: "90vh",
                    display: "flex", flexDirection: "column",
                    background: "var(--bm-bg-1, #0c1828)",
                    border: "1px solid var(--bm-border)",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <div style={{
                    padding: "14px 18px", display: "flex",
                    alignItems: "center", justifyContent: "space-between",
                    borderBottom: "1px solid var(--bm-border-soft)",
                    gap: 10,
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <CheckCircle2 size={18} color="var(--bm-gold)" />
                        <div style={{ minWidth: 0 }}>
                            <div className="bm-serif" data-testid="checklist-run-title" style={{ fontSize: 17, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {run.template_name}
                            </div>
                            <div style={{ fontSize: 11, color: "var(--bm-text-muted)" }}>
                                {isComplete
                                    ? `Completed · ${(run.completed_at || "").slice(0, 10)}`
                                    : `Started ${(run.started_at || "").slice(0, 10)}`}
                            </div>
                        </div>
                    </div>
                    <button
                        data-testid="checklist-run-close-btn"
                        className="bm-btn bm-btn-ghost"
                        onClick={onClose}
                        style={{ padding: 6 }}
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Progress */}
                <div style={{ padding: "10px 18px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                        <div style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>
                            <span data-testid="checklist-run-progress" style={{ color: "var(--bm-gold)" }}>{done}/{total}</span> items complete
                        </div>
                        <div style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>{pct}%</div>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                        <div
                            data-testid="checklist-run-progress-bar"
                            style={{ width: `${pct}%`, height: "100%", background: "var(--bm-gold)", transition: "width 250ms ease" }}
                        />
                    </div>
                </div>

                {err && (
                    <div data-testid="checklist-run-error" style={{ margin: "10px 18px 0", padding: 10, fontSize: 13, color: "var(--bm-red, #d97a7a)" }}>
                        {err}
                    </div>
                )}

                {/* Items */}
                <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px" }}>
                    {run.ticks.map((t) => (
                        <TickRow
                            key={t.item_id}
                            tick={t}
                            disabled={isComplete || saving}
                            expanded={expandedItem === t.item_id}
                            noteDraft={noteDrafts[t.item_id] ?? t.note ?? ""}
                            onExpand={() => setExpandedItem(expandedItem === t.item_id ? null : t.item_id)}
                            onNoteChange={(v) => setNoteDrafts({ ...noteDrafts, [t.item_id]: v })}
                            onToggle={() => tick(t.item_id, !t.done, noteDrafts[t.item_id] ?? t.note)}
                            onNoteSave={() => tick(t.item_id, t.done, noteDrafts[t.item_id] ?? "")}
                        />
                    ))}
                </div>

                {/* Footer */}
                {!isComplete && (
                    <div style={{
                        padding: "12px 18px", display: "flex",
                        alignItems: "center", justifyContent: "space-between",
                        gap: 10,
                        borderTop: "1px solid var(--bm-border-soft)",
                    }}>
                        <div style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>
                            {done === total ? "All items ticked — ready to finalize." : "Progress is saved automatically."}
                        </div>
                        <button
                            data-testid="checklist-run-finalize-btn"
                            className="bm-btn bm-btn-gold"
                            onClick={finalize}
                            disabled={saving || done === 0}
                            style={{ padding: "8px 18px", display: "inline-flex", alignItems: "center", gap: 6 }}
                        >
                            {saving ? <Loader2 size={12} className="bm-spin" /> : <Check size={12} />}
                            Complete run
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function TickRow({ tick, disabled, expanded, noteDraft, onExpand, onNoteChange, onToggle, onNoteSave }) {
    return (
        <div
            data-testid={`checklist-tick-${tick.item_id}`}
            style={{
                borderTop: "1px solid var(--bm-border-soft)",
                padding: "10px 0",
            }}
        >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <button
                    type="button"
                    data-testid={`checklist-tick-btn-${tick.item_id}`}
                    onClick={onToggle}
                    disabled={disabled}
                    aria-label={tick.done ? "Mark as not done" : "Mark as done"}
                    style={{
                        width: 22, height: 22, borderRadius: 4,
                        border: tick.done ? "1px solid var(--bm-gold)" : "1px solid var(--bm-border)",
                        background: tick.done ? "var(--bm-gold)" : "transparent",
                        cursor: disabled ? "default" : "pointer",
                        flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#1a1304",
                        marginTop: 2,
                    }}
                >
                    {tick.done && <Check size={14} strokeWidth={3} />}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        onClick={onExpand}
                        style={{
                            cursor: "pointer",
                            fontSize: 14, color: tick.done ? "var(--bm-text-muted)" : "var(--bm-text)",
                            textDecoration: tick.done ? "line-through" : "none",
                            lineHeight: 1.5,
                        }}
                    >
                        {tick.label}
                    </div>
                    {tick.note && !expanded && (
                        <div style={{ fontSize: 12, color: "var(--bm-text-dim)", marginTop: 4, fontStyle: "italic" }}>
                            ✎ {tick.note}
                        </div>
                    )}
                    {expanded && (
                        <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                            <input
                                data-testid={`checklist-tick-note-${tick.item_id}`}
                                type="text"
                                placeholder="Add a note (optional)"
                                value={noteDraft}
                                onChange={(e) => onNoteChange(e.target.value)}
                                disabled={disabled}
                                style={{
                                    flex: 1,
                                    padding: "6px 10px",
                                    fontSize: 13,
                                    background: "var(--bm-bg-0)",
                                    color: "var(--bm-text)",
                                    border: "1px solid var(--bm-border)",
                                    borderRadius: 6,
                                }}
                            />
                            <button
                                type="button"
                                data-testid={`checklist-tick-note-save-${tick.item_id}`}
                                className="bm-btn bm-btn-ghost"
                                onClick={onNoteSave}
                                disabled={disabled}
                                style={{ padding: "6px 10px" }}
                            >
                                <Save size={12} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
